import {Hierarchy, WorkClassDocument, WorkModelDocument} from "../types/pj1dbApiWork";
import {WORK_ATTRS} from "../constants/work";
import {Dict} from "../types/pj1dbApi";
import {WORK_SORT_ORDER, WorkSortOrderValue} from "../constants/constants";
import {FindWorkResponseData} from "./awsAmplifyUtil";
import {FindModelResponse} from "../types/pj1dbApiHandling";

export function extractDomain(email: string): string | null {
  const arr = email.split('@');
  return arr.length < 2 ? null : arr[1];
}

export function extractDomainFromWorkClass(work: WorkClassDocument): string | null {
  return extractDomain(work.attr.creator)
}

export interface PickSameDomainParams {
  workClasses: WorkClassDocument[],
  domain: string
}

export function pickSameDomain({workClasses, domain}: PickSameDomainParams): WorkClassDocument[] {
  return workClasses.filter((work) => extractDomainFromWorkClass(work) === domain);
}

export function excludeClassesWithChildren(classes: WorkClassDocument[]): WorkClassDocument[] {
  return classes.filter((work) => work.attr[WORK_ATTRS.children.key]?.length === 0);
}

export const DEFAULT_IMAGE_BLACK_URL = '/images/workdb/no_img_black.png';

export const DEFAULT_IMAGE_WHITE_URL = '/images/workdb/no_img_white.png';

export  const LOADING_IMAGE_URL = '/images/workdb/loading.png';

export function pickRepresentativeModels(workClass: WorkClassDocument, models: FindModelResponse[]): FindModelResponse[] {
  return Object
    .entries(workClass.url ?? {})
    .flatMap(([modelId]) => models.find((modelRes) => modelRes.modelId === modelId))
    .filter((modelRes): modelRes is FindModelResponse => modelRes !== undefined);
}

export function pickRepresentativeModel(workClass: WorkClassDocument, models: FindModelResponse[]): FindModelResponse | null {
  return pickRepresentativeModels(workClass, models).find(() => true) ?? null;
}

export function pickImageByClass(workClass: WorkClassDocument, models: FindModelResponse[]): string {
  const thumbnails = Object.entries(workClass.url ?? {})
    .flatMap(([modelId, images]) => {
      const modelOrUndefined = models.find((model) => model.modelId === modelId);
      if (modelOrUndefined === undefined) {
        return [] as const;
      }
      return modelOrUndefined.url.thumbnail.common30k
        ??  modelOrUndefined.url.thumbnail.original
        ?? []
    });

  return thumbnails.find(() => true) ?? DEFAULT_IMAGE_BLACK_URL;
}

export interface PickImageUrlWorkable {
  url?: Dict<string[]>;
}

export interface PickImageUrlOptions {
  defaultUrl?: string;
  sortOrder?: 'latest' | 'oldest';
}

export function pickImageUrl(work: PickImageUrlWorkable, options: PickImageUrlOptions = {}): string {
  return Object
      .entries(work.url ?? {})
      .sort(([aKey], [bKey]) => {
        // IDが時系列順になっていることを前提としている
        if (options.sortOrder === 'oldest') {
          return bKey.localeCompare(aKey);
        }

        return aKey.localeCompare(bKey);
      })
      .map(([_, value]) => {
        // 古い順に配列が並んでいることを前提としている
        if (options.sortOrder === 'oldest') {
          return value[0];
        }

        return value[value.length - 1];
      })
      ?.find(() => true)
    ?? options.defaultUrl
    ?? DEFAULT_IMAGE_BLACK_URL
}

export function pickImageUrlFromModel(work: WorkModelDocument, options: PickImageUrlOptions = {}): string {
  return pickImageUrl({
    url: {[work.attr["model-id"]]: work.url?.thumbnail.common30k ?? work.url?.thumbnail.original ?? []}
  }, options);
}

export function sortWorkClass(workClasses: WorkClassDocument[], sortOrder: WorkSortOrderValue): WorkClassDocument[] {
  const sortCriteria = {
    [WORK_SORT_ORDER.NAME.value]: (a: WorkClassDocument, b: WorkClassDocument) => (a.attr[WORK_ATTRS.nameJp.key] ?? '').localeCompare(b.attr[WORK_ATTRS.nameJp.key] ?? ''),
    [WORK_SORT_ORDER.CODE.value]: (a: WorkClassDocument, b: WorkClassDocument) => (a.attr[WORK_ATTRS.code.key] ?? '').localeCompare(b.attr[WORK_ATTRS.code.key] ?? ''),
    [WORK_SORT_ORDER.LATEST.value]: (a: WorkClassDocument, b: WorkClassDocument) => b.attr[WORK_ATTRS.created.key] - a.attr[WORK_ATTRS.created.key],
  } as const satisfies Record<WorkSortOrderValue, (a: WorkClassDocument, b: WorkClassDocument) => number>;

  return workClasses.sort(sortCriteria[sortOrder])
}

export function convertToWorkDocuments<T extends FindWorkResponseData<Hierarchy>>(work: T[]): Array<T['items'][string]> {
  return work.map((item) => Object.values(item.items)).flat() as Array<T['items'][string]>;
}
